import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, LanguagesIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { api } from '@/api'
import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import type { PortalTraining } from '@/api/types'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Markdown } from '@/components/markdown'
import { PageHeader } from '@/components/page-header'
import { VideoPlayer } from '@/components/training/video-player'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { formatDate } from '@/lib/format'
import { mediaUrl, PORTAL_TRAININGS_KEY, refreshDelay } from '@/lib/training-video'

export const Route = createFileRoute('/portal/formations/$videoId')({
  component: PortalTrainingPage,
})

/**
 * Une formation au portail (lot 38d ; le lien du courriel `training.published`) :
 * la présentation, le lecteur (sous-titres, vitesse, transcription) et, si la
 * même formation existe dans l'autre langue, le lien vers elle. Le jeton de
 * lecture se renouvelle avant d'expirer, pour une page restée ouverte.
 */
function PortalTrainingPage() {
  const { videoId } = Route.useParams()
  const t = msg().trainings.portal
  const training = useQuery({
    queryKey: [...PORTAL_TRAININGS_KEY, videoId],
    queryFn: () => portalApi.training(videoId),
    refetchInterval: (query) =>
      query.state.data ? refreshDelay(query.state.data.media_expires_at) : false,
  })

  const back = (
    <nav className="px-2">
      <Link to="/portal/formations" className="inline-flex items-center gap-1">
        <ArrowLeftIcon className="size-3.5" aria-hidden="true" />
        {t.back}
      </Link>
    </nav>
  )

  if (training.isPending) {
    return (
      <div className="space-y-5">
        <PageHeader eyebrow={back} title={t.title} />
        <Skeleton className="aspect-video w-full max-w-4xl rounded-lg" />
      </div>
    )
  }
  if (training.isError) {
    return (
      <div className="space-y-5">
        <PageHeader eyebrow={back} title={t.title} />
        <Frame>
          <FormError>
            {training.error instanceof ApiError && training.error.status === 404
              ? t.notFound
              : t.failed}
          </FormError>
        </Frame>
      </div>
    )
  }
  return <TrainingView training={training.data} back={back} />
}

function TrainingView({ training, back }: { training: PortalTraining; back: ReactNode }) {
  const t = msg().trainings.portal
  const url = (path: string) => mediaUrl(path, training.media_token, api.baseUrl)
  return (
    <div className="space-y-5" data-testid="portal-training">
      <PageHeader
        eyebrow={back}
        title={training.title}
        description={t.published(formatDate(training.published_at))}
        actions={
          training.other_language && training.other_language_id ? (
            <Link
              to="/portal/formations/$videoId"
              params={{ videoId: training.other_language_id }}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted"
              lang={training.other_language}
              data-testid="portal-training-other-language"
            >
              <LanguagesIcon className="size-4" aria-hidden="true" />
              {t.alsoIn[training.other_language]}
            </Link>
          ) : undefined
        }
      />
      <Frame className="space-y-4">
        {training.description.trim() && (
          <div data-testid="portal-training-description">
            <Markdown>{training.description}</Markdown>
          </div>
        )}
        <VideoPlayer
          key={training.id}
          title={training.title}
          language={training.language}
          sources={{
            video: url(training.video_url),
            poster: training.poster_url ? url(training.poster_url) : null,
            subtitles: training.subtitles_url ? url(training.subtitles_url) : null,
          }}
          className="max-w-4xl"
        />
      </Frame>
    </div>
  )
}
